import React from 'react';
import type { RequestOptions } from 'searchkit';
import c from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Text } from '@uncinc/uncinc-react-kitchen-sink';

import type {
  ParagraphContentSelection as Type,
} from '../../../../types/uncinc_profile/paragraphs';
import { ButtonWrapper } from '../../../../components';
import MetaSearch from '../../../../components/Search/MetaSearch';
import {
  constructQuery,
  constructAdditionalSelectionFilters,
  constructSortingList,
} from '../../../../components/Search/MetaSearch/metaParagraphsHelpers';

import './style.scss';

interface ParagraphContentSelectionProps {
  id?: string;
  entity: ImmutableEntity<Type>;
  isWide?: boolean;
}

const ParagraphContentSelection: React.FC<ParagraphContentSelectionProps> = ({
  id,
  entity,
  isWide = false,
}) => {
  const contentSelection = entity.toJS();
  const contentType = contentSelection.field_content_type[0].value;
  const langcode = contentSelection.langcode[0].value;
  const selectionMode = contentSelection.field_selection_mode_highlighted[0].value;

  const title = contentSelection.field_title[0]?.value;
  const body = contentSelection.field_body[0]?.processed || '';

  const link = contentSelection.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;

  const requestOptions: RequestOptions = {
    getBaseFilters: () => [
      constructQuery(contentType, langcode),
      constructAdditionalSelectionFilters(contentSelection),
    ],
  };

  let sortingList;
  if (selectionMode === 'manual') {
    // When the entities have been selected manually, we want to display
    // the results in the order they were selected in the CMS.
    // We do this by passing our own sortfunction through to the
    // SearchResults component in uncinc/react-searchkit.
    sortingList = constructSortingList(contentSelection);
  }

  const classNames = c(
    'Paragraph',
    'ParagraphMeta',
    'ParagraphContentSelection',
    `ParagraphContentSelection--${contentType}`,
  );

  return (
    <div id={id} className={classNames}>
      <Container>
        <div className="row">
          {(title || body) && (
            <div className={isWide ? 'col-12 col-md-10 col-lg-8' : 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-1'}>
              <div className="ParagraphContentSelection__body">
                <Text
                  title={title}
                  text={body}
                />
              </div>
            </div>
          )}

          <div className={isWide ? 'col-12' : 'col-12 col-md-10 offset-md-1'}>
            <MetaSearch
              requestOptions={requestOptions}
              contentType={contentType}
              sortingList={sortingList}
              // There's no limit in Drupal, however we should set a limit regardless.
              hitsPerPage={selectionMode === 'highlighted' ? 3 : 100}
            />

            {link && (
              <div className="ParagraphContentSelection__links">
                {linkUrl && (
                  <ButtonWrapper
                    className="ParagraphContentSelection__links__link"
                    type="secondary"
                    href={linkUrl}
                  >
                    {linkTitle}
                  </ButtonWrapper>
                )}
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphContentSelection;
